import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Bot, X, Send, Sparkles, Minimize2, Maximize2, Loader2, Paperclip, Mic, MicOff, Volume2, VolumeX, Brain, Link2, PenSquare, Hammer, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Whitelist of endpoint keys the AI is allowed to emit. Anything not in this map
// is rejected — Claude can't direct the UI to call arbitrary URLs.
const TUNE_ENDPOINTS = {
  'v12-safe-idle-target':   '/api/tunes/generate/v12-safe-idle-target',
  'v12-safe-idle-auto':     '/api/tunes/generate/v12-safe-idle-auto',
  'v12-safe-idle-lean125':  '/api/tunes/generate/v12-safe-idle-lean125',
  'v12-midextend':          '/api/tunes/generate/v12-midextend',
  'v12b-aggromid':          '/api/tunes/generate/v12b-aggromid',
  'v13-alphan':             '/api/tunes/generate/v13-alphan',
  'v12-safe':               '/api/tunes/generate/v12-safe',
  'build-next-revision':    '/api/tunes/build-next-revision',
  'v22-exhaust-knock-fix':  '/api/tunes/generate/v22-exhaust-knock-fix',
  'v23-oem-e85-86psi':      '/api/tunes/generate/v23-oem-e85-86psi',
  'v24-oem-e85-86psi-tight-sensors': '/api/tunes/generate/v24-oem-e85-86psi-tight-sensors',
  'v25-oem-e85-86psi-idle-fix': '/api/tunes/generate/v25-oem-e85-86psi-idle-fix',
  'v26-oem-e85-68psi':      '/api/tunes/generate/v26-oem-e85-68psi',
  'v27-oem-e85-78psi-short': '/api/tunes/generate/v27-oem-e85-78psi-short',
  'v27-oem-e85-78psi-full': '/api/tunes/generate/v27-oem-e85-78psi-full',
  'v28-e15-emergency':      '/api/tunes/generate/v28-e15-emergency',
  'v29-vanos-topend':       '/api/tunes/generate/v29-vanos-topend',
  'v30-n52-build-e85':      '/api/tunes/generate/v30-n52-build-e85',
};

// Pull a fenced ```ai-action {...}``` block (or bare ACTION:BUILD_TUNE / JSON-only)
// out of an assistant message. Returns null if none found or malformed.
function parseAiAction(text) {
  if (!text || typeof text !== 'string') return null;
  // Match ```ai-action ... ``` (loose newlines/whitespace)
  const m = text.match(/```\s*ai-action\s*\n?([\s\S]*?)```/i);
  if (!m) return null;
  try {
    const obj = JSON.parse(m[1].trim());
    if (obj?.action !== 'build_tune') return null;
    if (!obj.endpoint || !TUNE_ENDPOINTS[obj.endpoint]) return null;
    return {
      endpoint: obj.endpoint,
      url: TUNE_ENDPOINTS[obj.endpoint],
      body: obj.body || {},
      label: typeof obj.label === 'string' ? obj.label.slice(0, 80) : 'Build This Tune',
      summary: typeof obj.summary === 'string' ? obj.summary.slice(0, 240) : '',
      raw: m[0],
    };
  } catch {
    return null;
  }
}

// Strip the action block from the displayed text so users don't see raw JSON.
function stripActionBlock(text) {
  if (!text) return text;
  return text.replace(/```\s*ai-action\s*\n?[\s\S]*?```/gi, '').trim();
}

// Resilient fetch for long-running AI endpoints (Claude + full context can take 20-60s).
// - 90-second AbortController timeout (stops hanging connections but allows real responses)
// - Single automatic retry on network-layer failures (TypeError: Failed to fetch)
// - Does NOT retry on HTTP errors (4xx/5xx) — those are caller concerns
async function fetchWithRetry(url, opts = {}, { timeoutMs = 90000, retries = 2 } = {}) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const res = await fetch(url, { ...opts, signal: ctrl.signal });
      clearTimeout(timer);
      return res;
    } catch (err) {
      clearTimeout(timer);
      lastErr = err;
      // Only retry on AbortError (timeout) or TypeError (network blip) — NOT on HTTP errors
      const isNetworkError = err.name === 'AbortError' || err.name === 'TypeError';
      if (!isNetworkError || attempt >= retries) {
        // Rewrite the error message to something user-actionable
        if (err.name === 'AbortError') {
          throw new Error('Request took too long (>90s). Claude may be under heavy load — try again in a moment.');
        }
        if (err.name === 'TypeError' && err.message.includes('Failed to fetch')) {
          throw new Error('Network error reaching the AI. Check your connection, then try again.');
        }
        throw err;
      }
      // Retry path: brief backoff so we don't hammer on a flake
      await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
    }
  }
  throw lastErr;
}

// Context hints by tab — tells Claude what the user is looking at
const TAB_CONTEXT = {
  dashboard:   { label: 'Dashboard',   hint: 'vehicle status, credits, tune history' },
  upload:      { label: 'Upload',      hint: 'binary uploads and datalog uploads' },
  health:      { label: 'Health Check', hint: 'mechanical health gate (vacuum, knock, misfires)' },
  analysis:    { label: 'Analysis',    hint: 'datalog analysis results: AFR, LTFT, knock, HP curves' },
  tunes:       { label: 'My Tunes',    hint: 'generated tune .bin files (V1-V9) with version timeline' },
  flash:       { label: 'Flash ECU',   hint: 'physical flashing procedure, safety checks' },
  live:        { label: 'Live Data',   hint: 'live OBD stream via Thor WiFi / bluetooth adapter' },
  compare:     { label: 'Compare',     hint: 'binary diff tool between two tune versions' },
  ai:          { label: 'AI Copilot',  hint: 'dedicated full AI chat window' },
  pricing:     { label: 'Services',    hint: 'Stripe checkout for tuning packages and AI Tuner Pro Flasher' },
  help:        { label: 'Help',        hint: 'guide for common mechanical issues and tuning workflow' },
};

const QUICK_PROMPTS = {
  analysis: ["Summarize my last datalog", "Is my AFR healthy?", "What should I change next?"],
  tunes:    ["Compare V8 vs V9", "Why was V7 misleading?", "What should I flash next?"],
  health:   ["Interpret my idle health", "Is my motor ready to tune?"],
  flash:    ["Walk me through flashing safely", "What if it fails mid-flash?"],
  maps:     ["What does this fuel map tell me?", "Any dangerous cells I should watch?"],
  default:  ["What should I do next?", "Explain what I'm seeing", "Check my setup"],
};

// ----------------------------------------------------------------------
// CSV datalog summarizer — runs client-side so we can attach a tiny
// pre-digested block to Claude instead of a 400 KB CSV. Handles BMW INPA
// (lambda) + TunerPro (AFR) + generic naming conventions.
// ----------------------------------------------------------------------
function summarizeDatalogCsv(text, filename) {
  const lines = text.split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return { error: 'CSV has no data rows', filename, raw_preview: text.slice(0, 1000) };
  const splitCsv = (line) => {
    const out = []; let cur = ''; let inQ = false;
    for (const ch of line) {
      if (ch === '"') { inQ = !inQ; continue; }
      if (ch === ',' && !inQ) { out.push(cur); cur = ''; } else { cur += ch; }
    }
    out.push(cur);
    return out.map(s => s.trim().replace(/^"|"$/g, ''));
  };
  const headers = splitCsv(lines[0]);
  const rows = lines.slice(1).map(splitCsv);
  const colIdx = (name) => headers.findIndex(h => h.toLowerCase().includes(name.toLowerCase()));
  const tryCols = (names) => { for (const n of names) { const i = colIdx(n); if (i >= 0) return i; } return -1; };
  const num = (row, i) => { if (i < 0) return null; const v = parseFloat(row[i]); return Number.isFinite(v) ? v : null; };
  const stats = (arr) => {
    if (!arr.length) return null;
    const s = [...arr].sort((a,b)=>a-b);
    return { min: s[0], max: s[s.length-1], avg: arr.reduce((a,b)=>a+b,0)/arr.length, count: arr.length };
  };

  const iRpm    = tryCols(['Engine speed', 'RPM']);
  const iPedal  = tryCols(['Accelerator pedal', 'PEDAL', 'TPS']);
  const iAfr    = tryCols(['AFR (B1)', 'AFR B1', 'AFR']);
  const iLamB1  = tryCols(['Lambda value before catalyst bank 1', 'Lambda B1']);
  const iLamB2  = tryCols(['Lambda value before catalyst bank 2', 'Lambda B2']);
  const iKnock  = tryCols(['Knock signal cylinder 1', 'KNOCK- CYL 1', 'KNOCK']);
  const iTiming = tryCols(['Ignition timing cylinder 1', 'TIMING- CYL 1', 'Timing']);
  const iMaf    = tryCols(['Air mass', 'MAF']);
  const iIat    = tryCols(['Intake air temperature', 'IAT']);
  const iClt    = tryCols(['Coolant temperature', 'CLT']);
  const iTime   = tryCols(['Time']);

  // Lambda-only logs (BMW INPA) → convert to gas-scaled AFR (×14.7)
  const afrFromRow = (row) => {
    let a = num(row, iAfr);
    if (a != null && a > 5 && a < 25) return a;
    const lam = num(row, iLamB1);
    return lam != null && lam > 0.4 && lam < 1.5 ? lam * 14.7 : null;
  };

  const wotRows = [];
  const idleRows = [];
  for (const r of rows) {
    const rpm = num(r, iRpm);
    const pedal = num(r, iPedal);
    if (rpm == null) continue;
    if (rpm >= 2500 && pedal != null && pedal >= 90) wotRows.push(r);
    else if (rpm < 1100 && rpm > 500 && pedal != null && pedal < 2) idleRows.push(r);
  }

  const buckets = {};
  for (let lo = 4000; lo <= 7000; lo += 500) {
    const sub = wotRows.filter(r => { const rpm = num(r, iRpm); return rpm != null && rpm >= lo && rpm < lo + 500; });
    if (sub.length === 0) continue;
    const afrs = sub.map(afrFromRow).filter(x => x != null);
    const knocks = sub.map(r => num(r, iKnock)).filter(x => x != null);
    const tims = sub.map(r => num(r, iTiming)).filter(x => x != null);
    const mafs = sub.map(r => num(r, iMaf)).filter(x => x != null);
    buckets[`${lo}-${lo+500}`] = {
      samples: sub.length,
      afr_mean: afrs.length ? +(afrs.reduce((a,b)=>a+b,0)/afrs.length).toFixed(2) : null,
      knock_mean: knocks.length ? +(knocks.reduce((a,b)=>a+b,0)/knocks.length).toFixed(2) : null,
      knock_max: knocks.length ? +Math.max(...knocks).toFixed(2) : null,
      timing_mean: tims.length ? +(tims.reduce((a,b)=>a+b,0)/tims.length).toFixed(1) : null,
      maf_mean: mafs.length ? Math.round(mafs.reduce((a,b)=>a+b,0)/mafs.length) : null,
    };
  }

  const idleAfrs = idleRows.map(afrFromRow).filter(x => x != null);
  const wotAfrs = wotRows.map(afrFromRow).filter(x => x != null);
  const wotKnocks = wotRows.map(r => num(r, iKnock)).filter(x => x != null);
  const rpms = rows.map(r => num(r, iRpm)).filter(x => x != null);
  const times = rows.map(r => num(r, iTime)).filter(x => x != null);

  return {
    filename,
    total_rows: rows.length,
    columns: headers.length,
    time_span_s: times.length ? +(Math.max(...times) - Math.min(...times)).toFixed(2) : null,
    rpm_min: rpms.length ? Math.min(...rpms) : null,
    rpm_max: rpms.length ? Math.max(...rpms) : null,
    afr_units: iAfr >= 0 ? 'AFR' : (iLamB1 >= 0 ? 'lambda × 14.7 (gas-scaled)' : 'unknown'),
    idle_rows: idleRows.length,
    idle_afr_mean: stats(idleAfrs)?.avg?.toFixed(2),
    wot_rows: wotRows.length,
    wot_afr: stats(wotAfrs) && {
      min: +stats(wotAfrs).min.toFixed(2),
      max: +stats(wotAfrs).max.toFixed(2),
      mean: +stats(wotAfrs).avg.toFixed(2),
    },
    wot_knock: stats(wotKnocks) && {
      mean: +stats(wotKnocks).avg.toFixed(2),
      max: +stats(wotKnocks).max.toFixed(2),
    },
    wot_buckets_500rpm: buckets,
  };
}


export default function ClaudeFAB({ activeTab, user, vehicle, tunes }) {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [slowHint, setSlowHint] = useState(false);  // flips true after 12s of waiting
  const [liveStatus, setLiveStatus] = useState(''); // rotating status from /chat/stream SSE
  const [sessionId, setSessionId] = useState(null);
  // New: image attachment, voice recording, TTS autoplay
  const [pendingImage, setPendingImage] = useState(null);   // { file, previewUrl }
  const [pendingCsv, setPendingCsv] = useState(null);       // { file, summary }
  const [recording, setRecording] = useState(false);
  const [speakReplies, setSpeakReplies] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [secondOpinionLoading, setSecondOpinionLoading] = useState(null); // index of message being verified
  const [provider, setProvider] = useState(() => {
    try { return localStorage.getItem('copilot_provider') || 'claude'; }
    catch { return 'claude'; }
  });
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const currentAudioRef = useRef(null);

  const ctx = TAB_CONTEXT[activeTab] || { label: 'this page', hint: '' };
  const prompts = QUICK_PROMPTS[activeTab] || QUICK_PROMPTS.default;

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const getAuthHeaders = useCallback(() => {
    const token = (() => { try { return localStorage.getItem('auth_token'); } catch { return null; } })();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // Cross-device chat sync — on open, if we have no session loaded, grab the
  // user's most-recent session from the backend and load its full history.
  // This is what lets you switch from iPhone → laptop mid-tune-iteration and
  // pick up exactly where you left off with Claude.
  const loadLatestSession = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers.Authorization) return; // anonymous = no cross-device sync
      const r = await fetch(`${API_URL}/api/ai/sessions`, { headers });
      if (!r.ok) return;
      const sessions = await r.json();
      if (!Array.isArray(sessions) || sessions.length === 0) return;
      const latest = sessions[0]; // backend sorts by updated_at desc
      if (!latest?.session_id) return;
      const hr = await fetch(`${API_URL}/api/ai/history/${latest.session_id}`, { headers });
      if (!hr.ok) return;
      const history = await hr.json();
      if (!Array.isArray(history) || history.length === 0) return;
      setSessionId(latest.session_id);
      setMessages(history.map(m => ({ role: m.role, text: m.content })));
    } catch {
      // silent — we'll just start a fresh session
    }
  }, [getAuthHeaders]);

  // Trigger a one-time load on first open per page-mount.
  const loadedOnceRef = useRef(false);
  useEffect(() => {
    if (open && !loadedOnceRef.current && messages.length === 0) {
      loadedOnceRef.current = true;
      loadLatestSession();
    }
  }, [open, messages.length, loadLatestSession]);

  const startNewChat = useCallback(() => {
    if (!window.confirm('Start a new chat? Your existing conversation stays saved — switch back to it from any device by reopening Claude.')) return;
    setSessionId(null);
    setMessages([]);
    setInput('');
    setPendingImage(null);
    setPendingCsv(null);
  }, []);

  const speakText = useCallback(async (text) => {
    if (!text) return;
    try {
      if (currentAudioRef.current) { currentAudioRef.current.pause(); currentAudioRef.current = null; }
      const res = await fetchWithRetry(`${API_URL}/api/ai/voice/speak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ text: text.slice(0, 4000), voice: 'onyx', model: 'tts-1' }),
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      currentAudioRef.current = audio;
      audio.onended = () => URL.revokeObjectURL(url);
      await audio.play();
    } catch (e) { console.error('[ClaudeFAB] TTS playback failed:', e); }
  }, [getAuthHeaders]);

  const getSecondOpinion = useCallback(async (index) => {
    setSecondOpinionLoading(index);
    try {
      // Find the preceding user message
      const priorUser = [...messages.slice(0, index)].reverse().find(m => m.role === 'user');
      const assistantMsg = messages[index];
      const res = await fetchWithRetry(`${API_URL}/api/ai/second-opinion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          original_question: priorUser?.text || '(no prior question found)',
          claude_response: assistantMsg.text,
          context: `tab=${ctx.label}${vehicle ? `, vehicle=${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.engine}` : ''}`,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessages((m) => {
          const copy = [...m];
          copy[index] = { ...copy[index], secondOpinion: data.second_opinion };
          return copy;
        });
      }
    } catch (e) { console.error('[ClaudeFAB] second opinion failed:', e); }
    setSecondOpinionLoading(null);
  }, [messages, getAuthHeaders, ctx.label, vehicle]);

  const sendMessage = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if ((!text && !pendingImage && !pendingCsv) || sending) return;

    const contextLine = `[Context: user is on the "${ctx.label}" tab (${ctx.hint})${
      vehicle ? `, vehicle: ${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.engine}` : ''
    }${tunes?.length ? `, has ${tunes.length} tune versions` : ''}]`;

    // Embed CSV summary as a pre-digested JSON block so Claude can parse without
    // 400 KB of raw rows blowing past context. Done in the FAB so the formal
    // upload flow (with full backend analysis) stays the canonical path.
    let csvBlock = '';
    if (pendingCsv?.summary) {
      csvBlock = `\n\n[Attached datalog: ${pendingCsv.file.name}]\n` +
        '```json\n' + JSON.stringify(pendingCsv.summary, null, 2) + '\n```\n' +
        'Read this summary as the source of truth for the user\'s most recent pull. ' +
        'Reference specific RPM buckets, AFR, knock voltages, and timing values.';
    }
    const messageWithCtx = `${contextLine}${csvBlock}\n\n${text || (pendingImage ? '(see attached image)' : '(see attached datalog summary)')}`;

    setMessages((m) => [...m, {
      role: 'user',
      text: text || (pendingCsv ? `(datalog: ${pendingCsv.file.name})` : '(image)'),
      imagePreview: pendingImage?.previewUrl,
      csvName: pendingCsv?.file?.name,
    }]);
    setInput('');
    setSending(true);
    setSlowHint(false);
    const slowTimer = setTimeout(() => setSlowHint(true), 12000);
    const imageToSend = pendingImage?.file;
    setPendingImage(null);
    setPendingCsv(null);

    const mode = activeTab === 'compare' ? 'compare'
      : activeTab === 'analysis' ? 'advise'
      : activeTab === 'health' ? 'validate'
      : 'general';

    try {
      let data;
      if (imageToSend) {
        const fd = new FormData();
        fd.append('message', messageWithCtx);
        fd.append('mode', mode);
        if (sessionId) fd.append('session_id', sessionId);
        fd.append('image', imageToSend, imageToSend.name);
        const res = await fetchWithRetry(`${API_URL}/api/ai/chat-with-image`, {
          method: 'POST',
          headers: getAuthHeaders(),
          // NO credentials: 'include' — we use Bearer tokens, not cookies.
          // Including it forces CORS credentials mode which fails when the
          // CDN returns `Access-Control-Allow-Origin: *` (which it does on
          // production). Caused "Failed to fetch" / "Network error reaching the AI".
          body: fd,
        });
        data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Chat failed');
      } else {
        // Stream from /chat/stream — SSE with heartbeats every 8s prevents the
        // Cloudflare 30s edge-timeout that used to surface as "Failed to fetch"
        // on long datalog/binary analyses. The user also sees rotating status
        // messages so a 15s wait feels like 5s.
        const res = await fetch(`${API_URL}/api/ai/chat/stream`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...getAuthHeaders() },
          body: JSON.stringify({ message: messageWithCtx, session_id: sessionId, mode, provider }),
        });
        if (!res.ok || !res.body) {
          // Server returned a hard error before the stream even started
          let detail = `Chat failed (${res.status})`;
          try {
            const j = await res.json();
            // detail can be a string or an object (e.g., 402 insufficient_credits payload)
            if (j && j.detail) {
              detail = typeof j.detail === 'string' ? j.detail : JSON.stringify(j.detail);
            }
          } catch { /* ignore */ }
          throw new Error(detail);
        }
        // Parse SSE manually — no EventSource because EventSource is GET-only.
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let done = false;
        while (!done) {
          const { value, done: readerDone } = await reader.read();
          if (readerDone) break;
          buffer += decoder.decode(value, { stream: true });
          // Each SSE event is separated by a blank line
          let evtEnd;
          while ((evtEnd = buffer.indexOf('\n\n')) !== -1) {
            const raw = buffer.slice(0, evtEnd);
            buffer = buffer.slice(evtEnd + 2);
            const lines = raw.split('\n');
            let evtName = 'message';
            let dataStr = '';
            for (const line of lines) {
              if (line.startsWith('event:')) evtName = line.slice(6).trim();
              else if (line.startsWith('data:')) dataStr += line.slice(5).trim();
            }
            if (!dataStr) continue;
            let payload = {};
            try { payload = JSON.parse(dataStr); } catch { /* malformed — skip */ continue; }
            if (evtName === 'status') {
              setLiveStatus(payload.text || '');
            } else if (evtName === 'complete') {
              data = payload;
              done = true;
              break;
            } else if (evtName === 'error') {
              // payload.detail may be a string OR an object (402 insufficient_credits).
              const det = payload.detail;
              const msg = typeof det === 'string' ? det : JSON.stringify(det || { error: 'unknown' });
              throw new Error(msg);
            }
          }
        }
        if (!data) throw new Error('Stream ended without a response');
      }
      if (data.session_id) setSessionId(data.session_id);
      setMessages((m) => [...m, {
        role: 'assistant',
        text: data.response,
        resources: data.resources_used || [],
        billing: data.billing || null,
      }]);
      // Fire global event so the WalletBalanceBadge re-fetches the new balance.
      try { window.dispatchEvent(new CustomEvent('wallet:refresh')); } catch { /* noop */ }
      if (speakReplies && data.response) speakText(data.response);
    } catch (e) {
      // Intercept 402 insufficient_credits — prompt the user to top up.
      const msg = String(e.message || '');
      if (msg.includes('insufficient_credits') || msg.includes('Top up') || msg.includes('402')) {
        try {
          window.dispatchEvent(new CustomEvent('wallet:openTopUp', {
            detail: { message: 'AI Copilot credits exhausted. Top up to keep chatting.' },
          }));
        } catch { /* noop */ }
        setMessages((m) => [...m, {
          role: 'assistant',
          text: 'You\u0027re out of AI Copilot credits. Click the wallet badge in the top nav or use the top-up modal that just opened to add more. Your tunes, datalogs, and build buttons still work without credits.',
          error: true,
        }]);
      } else {
        setMessages((m) => [...m, { role: 'assistant', text: `Error: ${e.message}`, error: true }]);
      }
    } finally {
      clearTimeout(slowTimer);
      setSending(false);
      setSlowHint(false);
      setLiveStatus('');
    }
  };

  const onPickImage = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const name = (file.name || '').toLowerCase();
    const isCsv = /\.csv$/i.test(name) || /^text\/csv/i.test(file.type) || file.type === 'application/vnd.ms-excel';
    const isImage = /^image\/(jpe?g|png|webp)$/i.test(file.type);

    if (isCsv) {
      if (file.size > 10 * 1024 * 1024) {
        setMessages((m) => [...m, { role: 'assistant', text: 'CSV too large (max 10 MB).', error: true }]);
        return;
      }
      try {
        const text = await file.text();
        const summary = summarizeDatalogCsv(text, file.name);
        setPendingCsv({ file, summary });
      } catch (err) {
        console.error('[ClaudeFAB] CSV parse failed:', err);
        setMessages((m) => [...m, { role: 'assistant', text: `Could not read CSV: ${err.message}`, error: true }]);
      }
      e.target.value = '';
      return;
    }

    if (!isImage) {
      setMessages((m) => [...m, { role: 'assistant', text: 'Attach JPEG/PNG/WEBP image or a .csv datalog.', error: true }]);
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setMessages((m) => [...m, { role: 'assistant', text: 'Image too large (max 8 MB).', error: true }]);
      return;
    }
    setPendingImage({ file, previewUrl: URL.createObjectURL(file) });
    e.target.value = '';
  }, []);

  const toggleRecord = useCallback(async () => {
    if (recording) {
      mediaRecorderRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      audioChunksRef.current = [];
      rec.ondataavailable = (ev) => { if (ev.data?.size) audioChunksRef.current.push(ev.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        setRecording(false);
        const blob = new Blob(audioChunksRef.current, { type: mime || 'audio/webm' });
        if (blob.size < 500) return;
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append('audio', blob, 'memo.webm');
          fd.append('language', 'en');
          const res = await fetchWithRetry(`${API_URL}/api/ai/voice/transcribe`, {
            method: 'POST',
            headers: getAuthHeaders(),
            // NO credentials: 'include' — Bearer-token auth. Keeping it would
            // collide with the CDN's `Access-Control-Allow-Origin: *` and cause
            // "Failed to fetch" in the browser (CORS credentialed-origin rule).
            body: fd,
          });
          const data = await res.json();
          if (res.ok && data.text) setInput((prev) => (prev ? `${prev} ${data.text}` : data.text));
        } catch (e) { console.error('[ClaudeFAB] voice transcribe failed:', e); }
        setTranscribing(false);
      };
      mediaRecorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', text: `Mic error: ${e.message}. Grant microphone permission.`, error: true }]);
    }
  }, [recording, getAuthHeaders]);

  // Global handoff: lets any component open the copilot with a pre-filled message.
  // The Quick Tuner Request form and the Free Analysis report both use this so
  // users land directly in a real AI conversation instead of a dead form.
  useEffect(() => {
    const api = {
      open: (text, { autoSend = true } = {}) => {
        setOpen(true);
        setMinimized(false);
        if (!text) return;
        if (autoSend) {
          // Defer one tick so the chat panel can render before we fire off the message.
          setTimeout(() => sendMessage(text), 50);
        } else {
          setInput(text);
        }
      },
    };
    window.tunerProChat = api;
    return () => {
      if (window.tunerProChat === api) delete window.tunerProChat;
    };
    // We intentionally skip `sendMessage` in deps — it closes over fresh state each call
    // and re-registering the global on every keystroke would be wasteful.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* Floating Action Button */}
      {!open && (
        <button
          onClick={() => { setOpen(true); setMinimized(false); }}
          className="fixed bottom-6 right-6 z-[90] w-14 h-14 rounded-full bg-gradient-to-br from-[#FF5E1A] to-[#FF7A3D] shadow-[0_0_30px_rgba(255,94,26,0.5)] hover:shadow-[0_0_50px_rgba(255,94,26,0.8)] flex items-center justify-center transition-all group"
          title="Ask Claude about this page"
          data-testid="claude-fab-open"
        >
          <Bot className="w-6 h-6 text-black group-hover:scale-110 transition-transform" />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full border-2 border-[#050505] animate-pulse" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className={`fixed right-6 z-[95] bg-[#050505] border border-[#FF5E1A]/30 rounded-sm shadow-2xl backdrop-blur-xl transition-all ${
            minimized ? 'bottom-6 w-64 h-14' : 'bottom-6 w-96 h-[560px] max-h-[calc(100vh-48px)]'
          }`}
          data-testid="claude-fab-panel"
        >
          {/* Header */}
          <div
            onClick={() => minimized && setMinimized(false)}
            className={`flex items-center justify-between p-3 border-b border-[#27272A] bg-gradient-to-r from-[#FF5E1A]/15 to-transparent ${
              minimized ? 'cursor-pointer' : ''
            }`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded-sm bg-gradient-to-br from-[#FF5E1A] to-[#FF7A3D] flex items-center justify-center flex-shrink-0 shadow-[0_0_15px_rgba(255,94,26,0.4)]">
                <Bot className="w-4 h-4 text-black" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-['Outfit'] font-bold text-white">{provider === 'glm' ? 'GLM-5.2 Copilot' : 'Claude Copilot'}</p>
                <p className="text-[10px] text-[#FF5E1A] font-['JetBrains_Mono'] truncate">
                  {ctx.label} context
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {/* Model selector — Claude (default) vs GLM-5.2 (beta) */}
              <div
                className="hidden sm:flex items-center gap-0.5 mr-1 p-0.5 bg-zinc-900 rounded-sm border border-zinc-800"
                title="Model selector"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  data-testid="copilot-model-claude"
                  onClick={(e) => {
                    e.stopPropagation();
                    setProvider('claude');
                    try { localStorage.setItem('copilot_provider', 'claude'); } catch { /* ignore */ }
                  }}
                  className={`px-2 py-0.5 text-[10px] font-['JetBrains_Mono'] rounded-sm transition-colors ${provider === 'claude' ? 'bg-[#FF5E1A] text-black font-bold' : 'text-zinc-400 hover:text-white'}`}
                >
                  Claude
                </button>
                <button
                  data-testid="copilot-model-glm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setProvider('glm');
                    try { localStorage.setItem('copilot_provider', 'glm'); } catch { /* ignore */ }
                  }}
                  className={`px-2 py-0.5 text-[10px] font-['JetBrains_Mono'] rounded-sm transition-colors ${provider === 'glm' ? 'bg-cyan-500 text-black font-bold' : 'text-zinc-400 hover:text-white'}`}
                  title="GLM-5.2 from Z.AI — coding-focused, 1M context, beta only"
                >
                  GLM
                </button>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); startNewChat(); }}
                className="p-1.5 hover:bg-zinc-800 rounded-sm"
                title="New chat (current one stays saved)"
                data-testid="claude-fab-new-chat"
              >
                <PenSquare className="w-3.5 h-3.5 text-zinc-400" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setMinimized((v) => !v); }}
                className="p-1.5 hover:bg-zinc-800 rounded-sm"
                title={minimized ? "Expand" : "Minimize"}
                data-testid="claude-fab-minimize"
              >
                {minimized ? <Maximize2 className="w-3.5 h-3.5 text-zinc-400" /> : <Minimize2 className="w-3.5 h-3.5 text-zinc-400" />}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setOpen(false); }}
                className="p-1.5 hover:bg-zinc-800 rounded-sm"
                data-testid="claude-fab-close"
              >
                <X className="w-3.5 h-3.5 text-zinc-400" />
              </button>
            </div>
          </div>

          {!minimized && (
            <>
              {/* Messages */}
              <div
                ref={scrollRef}
                className="p-3 overflow-y-auto space-y-3"
                style={{ height: 'calc(100% - 155px)' }}
                data-testid="claude-fab-messages"
              >
                {messages.length === 0 && (
                  <div className="space-y-3">
                    <div className="bg-[#FF5E1A]/5 border border-[#FF5E1A]/20 rounded-sm p-3">
                      <div className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 text-[#FF5E1A] flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-zinc-300">
                          I can see you&apos;re on <span className="text-[#FF5E1A] font-bold">{ctx.label}</span>.
                          Ask me anything — I have access to your binaries, datalogs, maps, and tune history.
                        </p>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-['JetBrains_Mono']">Quick prompts</p>
                      {prompts.map((p) => (
                        <button
                          key={p}
                          onClick={() => sendMessage(p)}
                          className="w-full text-left text-xs text-zinc-300 bg-[#0A0A0A] hover:bg-[#111111] border border-[#27272A] hover:border-[#FF5E1A]/40 rounded-sm px-3 py-2 transition-all"
                          data-testid={`claude-fab-quick-${p.slice(0, 20)}`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[85%] rounded-sm px-3 py-2 text-xs ${
                        m.role === 'user'
                          ? 'bg-[#FF5E1A]/15 border border-[#FF5E1A]/30 text-white'
                          : m.error
                            ? 'bg-red-500/10 border border-red-500/30 text-red-300'
                            : 'bg-[#0A0A0A] border border-[#27272A] text-zinc-300'
                      }`}
                    >
                      {m.imagePreview && (
                        <img
                          src={m.imagePreview}
                          alt="attached"
                          className="max-w-full max-h-40 rounded-sm mb-2 border border-[#27272A]"
                        />
                      )}
                      <p className="whitespace-pre-wrap leading-relaxed">{m.role === 'assistant' ? stripActionBlock(m.text) : m.text}</p>
                      {m.role === 'assistant' && !m.error && (() => {
                        const action = parseAiAction(m.text);
                        if (!action) return null;
                        const isBuilding = m.tuneBuilding;
                        const builtResult = m.tuneBuiltResult;
                        return (
                          <div className="mt-3 p-3 bg-gradient-to-br from-[#FF5E1A]/10 to-amber-500/5 border border-[#FF5E1A]/40 rounded-sm" data-testid={`claude-fab-build-action-${i}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <Hammer className="w-3.5 h-3.5 text-[#FF5E1A]" />
                              <span className="text-[10px] uppercase tracking-wider text-[#FF5E1A] font-bold font-['JetBrains_Mono']">AI Build Action</span>
                            </div>
                            {action.summary && (
                              <p className="text-[11px] text-zinc-300 italic mb-2">&ldquo;{action.summary}&rdquo;</p>
                            )}
                            <div className="text-[10px] text-zinc-500 font-['JetBrains_Mono'] mb-2">
                              endpoint: <span className="text-cyan-300">{action.endpoint}</span>
                              {Object.keys(action.body || {}).length > 0 && (
                                <> · body: <span className="text-cyan-300">{JSON.stringify(action.body)}</span></>
                              )}
                            </div>
                            {builtResult ? (
                              <div className="space-y-1.5">
                                <div className="flex items-center gap-1.5 text-emerald-300 text-[11px]">
                                  <CheckCircle2 className="w-3.5 h-3.5" />
                                  <span>Built: {builtResult.version || builtResult.filename}</span>
                                </div>
                                {builtResult.sha256 && (
                                  <div className="text-[10px] text-zinc-500 font-['JetBrains_Mono']">sha: {String(builtResult.sha256).slice(0, 24)}…</div>
                                )}
                                {(builtResult.clean_download_url || builtResult.download_url) && (
                                  <a
                                    href={`${API_URL}${builtResult.clean_download_url || builtResult.download_url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-[11px] text-cyan-300 hover:text-cyan-200 underline"
                                    data-testid={`claude-fab-build-download-${i}`}
                                  >
                                    Download {builtResult.clean_filename || builtResult.filename}
                                  </a>
                                )}
                                {builtResult.deduped && (
                                  <div className="text-[10px] text-amber-400 italic">⚠ Identical bytes to an existing bin — using existing file.</div>
                                )}
                              </div>
                            ) : (
                              <button
                                type="button"
                                disabled={isBuilding}
                                onClick={async () => {
                                  // Client-side guard for the bi-directional idle target endpoint.
                                  // Backend caps single-revision delta at 2.5 AFR; show a friendly
                                  // alert before the network round-trip if user/AI exceeds it.
                                  if (action.endpoint === 'v12-safe-idle-target') {
                                    const tgt = Number(action.body?.target_afr);
                                    const cur = Number(action.body?.current_afr);
                                    if (Number.isFinite(tgt) && Number.isFinite(cur)) {
                                      const gap = Math.abs(tgt - cur);
                                      if (gap < 0.1) {
                                        window.alert(`Current ${cur} AFR and target ${tgt} AFR are too close (<0.1). No tune needed.`);
                                        return;
                                      }
                                      if (gap > 2.5) {
                                        window.alert(
                                          `Change too big for a single tune: ${gap.toFixed(1)} AFR delta.\n\n` +
                                          `Cap per revision is 2.5 AFR to keep adaptations sane. ` +
                                          `Build an intermediate first (e.g., ${cur} → ${(cur + (tgt > cur ? 2.0 : -2.0)).toFixed(1)} AFR), ` +
                                          `flash it, log it, then iterate to ${tgt}.`
                                        );
                                        return;
                                      }
                                    }
                                  }
                                  setMessages((prev) => prev.map((mm, ii) => ii === i ? { ...mm, tuneBuilding: true, tuneBuildError: null } : mm));
                                  try {
                                    const token = localStorage.getItem('auth_token');
                                    const res = await fetch(`${API_URL}${action.url}`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                                      body: JSON.stringify(action.body || {}),
                                    });
                                    // Read as text first — survives the case where a browser extension or
                                    // dev HMR has already consumed the body stream, which makes res.json() throw
                                    // "body stream already read". text() is more forgiving.
                                    const raw = await res.text();
                                    let d;
                                    try { d = raw ? JSON.parse(raw) : {}; } catch { d = { detail: raw || `HTTP ${res.status}` }; }
                                    if (!res.ok) {
                                      const detail = d.detail;
                                      const msg = typeof detail === 'string' ? detail : (detail ? JSON.stringify(detail) : `HTTP ${res.status}`);
                                      throw new Error(msg);
                                    }
                                    const result = d.tune || d;
                                    setMessages((prev) => prev.map((mm, ii) => ii === i ? { ...mm, tuneBuilding: false, tuneBuiltResult: result } : mm));
                                  } catch (err) {
                                    setMessages((prev) => prev.map((mm, ii) => ii === i ? { ...mm, tuneBuilding: false, tuneBuildError: err.message } : mm));
                                    window.alert(`Build failed: ${err.message}`);
                                  }
                                }}
                                className="w-full px-3 py-2 bg-[#FF5E1A] hover:bg-[#FF7A3A] disabled:bg-zinc-700 disabled:text-zinc-500 disabled:cursor-not-allowed text-black font-bold text-xs rounded-sm transition-colors uppercase tracking-wider font-['JetBrains_Mono'] flex items-center justify-center gap-2"
                                data-testid={`claude-fab-build-button-${i}`}
                              >
                                {isBuilding ? (
                                  <><Loader2 className="w-3.5 h-3.5 animate-spin" />Building…</>
                                ) : (
                                  <><Hammer className="w-3.5 h-3.5" />{action.label}</>
                                )}
                              </button>
                            )}
                            {m.tuneBuildError && (
                              <div className="mt-2 text-[11px] text-red-300">{m.tuneBuildError}</div>
                            )}
                          </div>
                        );
                      })()}
                      {m.resources?.length > 0 && (
                        <div
                          data-testid={`claude-fab-resources-${i}`}
                          className="mt-2 pt-2 border-t border-[#27272A] flex items-center gap-1.5 flex-wrap"
                        >
                          <span className="text-[9px] text-zinc-500 uppercase tracking-wider font-['JetBrains_Mono']">
                            Powered by:
                          </span>
                          {m.resources.map((r) => (
                            <a
                              key={r.id}
                              href={r.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={r.url}
                              className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-sm border border-cyan-500/30 text-cyan-300 hover:border-cyan-400/60 hover:bg-cyan-500/10 transition-colors font-['JetBrains_Mono']"
                            >
                              <Link2 className="w-2.5 h-2.5" />
                              {r.title}
                            </a>
                          ))}
                        </div>
                      )}
                      {m.role === 'assistant' && !m.error && (
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                          <button
                            onClick={() => speakText(m.text)}
                            className="text-[10px] text-zinc-500 hover:text-[#FF5E1A] flex items-center gap-1"
                            data-testid={`claude-fab-speak-${i}`}
                          >
                            <Volume2 className="w-3 h-3" />Play audio
                          </button>
                          <button
                            onClick={() => getSecondOpinion(i)}
                            disabled={secondOpinionLoading === i || !!m.secondOpinion}
                            className="text-[10px] text-zinc-500 hover:text-cyan-400 flex items-center gap-1 disabled:opacity-50"
                            data-testid={`claude-fab-second-opinion-${i}`}
                          >
                            {secondOpinionLoading === i
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : <Brain className="w-3 h-3" />}
                            {m.secondOpinion ? 'GPT verified' : 'Second opinion (GPT-5.2)'}
                          </button>
                        </div>
                      )}
                      {m.secondOpinion && (
                        <div className="mt-2 pt-2 border-t border-cyan-500/20">
                          <div className="flex items-center gap-1 mb-1">
                            <Brain className="w-3 h-3 text-cyan-400" />
                            <span className="text-[10px] font-bold text-cyan-400 uppercase tracking-wider">
                              GPT-5.2 Second Opinion
                            </span>
                          </div>
                          <p className="whitespace-pre-wrap leading-relaxed text-cyan-100/90 text-[11px]">
                            {m.secondOpinion}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {sending && (
                  <div className="flex items-center gap-2 text-xs text-zinc-500" data-testid="copilot-thinking">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    <span>{liveStatus || (slowHint ? 'Still working… full datalog + vehicle context takes 20-40s' : 'Claude is thinking…')}</span>
                  </div>
                )}
              </div>

              {/* Input */}
              <div className="border-t border-[#27272A] p-3 space-y-2">
                {/* Image preview + recording indicator row */}
                {(pendingImage || pendingCsv || recording || transcribing) && (
                  <div className="flex items-center gap-2 text-[10px] flex-wrap">
                    {pendingImage && (
                      <div className="flex items-center gap-1 bg-[#0A0A0A] border border-[#FF5E1A]/30 rounded-sm px-2 py-1">
                        <img src={pendingImage.previewUrl} alt="preview" className="w-6 h-6 object-cover rounded-sm" />
                        <span className="text-zinc-400">attached</span>
                        <button
                          onClick={() => setPendingImage(null)}
                          className="text-zinc-500 hover:text-red-400"
                          data-testid="claude-fab-remove-image"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    {pendingCsv && (
                      <div className="flex items-center gap-1 bg-[#0A0A0A] border border-emerald-500/40 rounded-sm px-2 py-1" data-testid="claude-fab-csv-pill">
                        <span className="text-emerald-400 font-['JetBrains_Mono']">csv</span>
                        <span className="text-zinc-300 max-w-[180px] truncate" title={pendingCsv.file.name}>{pendingCsv.file.name}</span>
                        <span className="text-zinc-500">· {pendingCsv.summary?.wot_rows ?? 0} WOT rows</span>
                        <button
                          onClick={() => setPendingCsv(null)}
                          className="text-zinc-500 hover:text-red-400 ml-1"
                          data-testid="claude-fab-remove-csv"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    {recording && (
                      <span className="flex items-center gap-1 text-red-400">
                        <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                        Recording… tap stop
                      </span>
                    )}
                    {transcribing && (
                      <span className="flex items-center gap-1 text-zinc-400">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Transcribing…
                      </span>
                    )}
                  </div>
                )}

                <div className="flex gap-2 items-center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,.csv,text/csv,application/vnd.ms-excel"
                    onChange={onPickImage}
                    className="hidden"
                    data-testid="claude-fab-file-input"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={sending}
                    className={`p-2 rounded-sm border transition-colors ${
                      pendingImage || pendingCsv
                        ? 'border-[#FF5E1A]/60 text-[#FF5E1A] bg-[#FF5E1A]/10'
                        : 'border-[#27272A] text-zinc-400 hover:border-[#FF5E1A]/40 hover:text-[#FF5E1A]'
                    }`}
                    title="Attach image (scanner photo, dash) or datalog CSV"
                    data-testid="claude-fab-attach"
                  >
                    <Paperclip className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={toggleRecord}
                    disabled={sending || transcribing}
                    className={`p-2 rounded-sm border transition-colors ${
                      recording
                        ? 'border-red-500/60 text-red-400 bg-red-500/10 animate-pulse'
                        : 'border-[#27272A] text-zinc-400 hover:border-[#FF5E1A]/40 hover:text-[#FF5E1A]'
                    }`}
                    title={recording ? "Stop recording" : "Voice memo (Whisper)"}
                    data-testid="claude-fab-mic"
                  >
                    {recording ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
                  </button>
                  <button
                    onClick={() => setSpeakReplies((v) => !v)}
                    className={`p-2 rounded-sm border transition-colors ${
                      speakReplies
                        ? 'border-emerald-500/60 text-emerald-400 bg-emerald-500/10'
                        : 'border-[#27272A] text-zinc-400 hover:border-[#FF5E1A]/40 hover:text-[#FF5E1A]'
                    }`}
                    title={speakReplies ? "Turn off spoken replies" : "Speak replies (TTS)"}
                    data-testid="claude-fab-tts-toggle"
                  >
                    {speakReplies ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
                  </button>
                  <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                    placeholder={pendingImage ? "Describe what to look at…" : `Ask about ${ctx.label.toLowerCase()}…`}
                    disabled={sending}
                    className="flex-1 bg-[#0A0A0A] border border-[#27272A] focus:border-[#FF5E1A]/60 rounded-sm px-3 py-2 text-xs text-white placeholder-zinc-600 outline-none transition-colors"
                    data-testid="claude-fab-input"
                  />
                  <button
                    onClick={() => sendMessage()}
                    disabled={(!input.trim() && !pendingImage && !pendingCsv) || sending}
                    className="px-3 py-2 bg-[#FF5E1A] hover:bg-[#FF7A3D] disabled:opacity-40 disabled:cursor-not-allowed text-black rounded-sm transition-colors"
                    data-testid="claude-fab-send"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-[9px] text-zinc-600 text-center">
                  Vision · Voice · Context-aware · Claude 4.5 + Whisper + OpenAI TTS
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
